/**
 * Promotion Tracking
 *
 * Shared utility for GA4 view_promotion / select_promotion dataLayer events.
 * Stores the last clicked promotion in localStorage so downstream pages (e.g. PDP)
 * can read and report it.
 *
 * Two tracking modes are supported — the mode is auto-detected from the options shape.
 *
 * MODE 1 — Slider/banner sections. Pass `containerSelectors` (array) + a promotions array:
 *
 *   <script>
 *     window.PromotionTrackingQueue = window.PromotionTrackingQueue || [];
 *     window.PromotionTrackingQueue.push({
 *       promotions: [...],
 *       containerSelectors: ['.my-desktop-slider', '.my-mobile-slider'],
 *       slideSelector: '.slideshow__slide',
 *       linkSelector: '.full-link'
 *     });
 *   <\/script>
 *
 * MODE 2 — Static grid / carousel sections. Pass `containerSelector` (string).
 * Each trackable item element must have data-promo-creative and data-promo-slot attributes:
 *
 *   <script>
 *     window.PromotionTrackingQueue = window.PromotionTrackingQueue || [];
 *     window.PromotionTrackingQueue.push({
 *       containerSelector: '#shopify-section-SECTION_ID',
 *       itemSelector: '.grid-item[data-promo-slot]',
 *       linkSelector: '.full-link',   // optional — restrict click trigger to a child element
 *       promotionName: 'My Campaign'
 *     });
 *   <\/script>
 *
 * promotion-tracking.js is loaded globally via theme.liquid — sections only push to the queue.
 *
 * Or call directly: window.PromotionTracking.init({ ... }) / window.PromotionTracking.initGrid({ ... })
 */
(function () {
  var STORAGE_KEYS = {
    promotion_id:   'lastClickedPromotionId',
    promotion_name: 'lastClickedPromotionName',
    creative_name:  'lastClickedCreativeName',
    creative_slot:  'lastClickedCreativeSlot',
  };

  function pushPromoEvent(eventName, promo) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      ecommerce: {
        promotion_id:   promo.promotion_id   || undefined,
        promotion_name: promo.promotion_name || undefined,
        creative_name:  promo.creative_name || undefined,
        creative_slot:  promo.creative_slot || undefined,
      }
    });
  }

  function initPromoTracking(options) {
    var promotions = options.promotions || [];
    var containerSelectors = options.containerSelectors || [];
    var slideSelector = options.slideSelector || '.slideshow__slide';
    var linkSelector = options.linkSelector || '.full-link';
    var fireAllOnLoad = options.fireAllOnLoad || false;
    var slideActiveClass = options.slideActiveClass || null;
    var observeViewport = options.observeViewport || false;

    if (promotions.length === 0) return;

    function setup() {
      // Guard against firing the initial view_promotion more than once when
      // multiple containerSelectors point to duplicate DOM trees (e.g. the
      // video slider has both .banner-desktop and .banner-mobile).
      var initialFired = false;

      containerSelectors.forEach(function (selector) {
        var container = document.querySelector(selector);
        if (!container) return;

        // Track slides that have already fired view_promotion so we never double-fire.
        var firedSlides = [];

        // initContainer() does all the real work: collect the Slick-rendered DOM,
        // register click/mutation handlers, and fire init view_promotion events.
        // When observeViewport is true this runs only once the container enters the
        // viewport, which also guarantees Slick has had time to fully initialise.
        function initContainer() {
          // Single-promotion sections (e.g. video slider): fire for promotions[0] now.
          if (!fireAllOnLoad && !slideActiveClass && !initialFired) {
            initialFired = true;
            pushPromoEvent('view_promotion', promotions[0]);
          }

          // Collect all real (non-cloned) slides and their matching promotion objects.
          var realSlides = [];
          var realPromos = [];
          var promoIdx = 0;
          container.querySelectorAll(slideSelector).forEach(function (slide) {
            if (slide.classList.contains('slick-cloned')) return;
            var promo = promotions[promoIdx++];
            if (!promo) return;
            realSlides.push(slide);
            realPromos.push(promo);
          });

          realSlides.forEach(function (slide, idx) {
            var promo = realPromos[idx];

            function fireViewIfNew() {
              if (firedSlides.indexOf(slide) !== -1) return;
              firedSlides.push(slide);
              pushPromoEvent('view_promotion', promo);
            }

            function firePeekIfNew() {
              // Fire view_promotion for the first slide after the active group (peek card).
              var nextSlide = realSlides[idx + 1];
              var nextPromo = realPromos[idx + 1];
              if (!nextSlide || !nextPromo) return;
              if (firedSlides.indexOf(nextSlide) !== -1) return;
              firedSlides.push(nextSlide);
              pushPromoEvent('view_promotion', nextPromo);
            }

            // Init view_promotion fires for already-visible slides.
            if (slideActiveClass) {
              if (slide.classList.contains(slideActiveClass)) {
                fireViewIfNew();
                var nextSlide = realSlides[idx + 1];
                if (nextSlide && !nextSlide.classList.contains(slideActiveClass)) {
                  firePeekIfNew();
                }
              }
            } else if (fireAllOnLoad) {
              if (slide.getAttribute('aria-hidden') !== 'true') {
                pushPromoEvent('view_promotion', promo);
              }
            }

            // select_promotion + localStorage on banner click.
            var link = slide.querySelector(linkSelector);
            if (link) {
              link.addEventListener('click', function () {
                pushPromoEvent('select_promotion', promo);
                try {
                  sessionStorage.setItem(STORAGE_KEYS.promotion_id,   promo.promotion_id   || '');
                  sessionStorage.setItem(STORAGE_KEYS.promotion_name, promo.promotion_name || '');
                  sessionStorage.setItem(STORAGE_KEYS.creative_name,  promo.creative_name  || '');
                  sessionStorage.setItem(STORAGE_KEYS.creative_slot,  promo.creative_slot  || '');
                } catch (_) {}
              });
            }

            // view_promotion when a slide transitions into view (ongoing navigation).
            var slideActiveCooldown = false;
            var observer = new MutationObserver(function (mutations) {
              mutations.forEach(function (mutation) {
                if (slideActiveClass) {
                  // Slick: fire when slick-active is newly added.
                  // Debounce because Slick can remove+re-add the class in one transition.
                  if (
                    mutation.attributeName === 'class' &&
                    slide.classList.contains(slideActiveClass) &&
                    !(mutation.oldValue || '').split(/\s+/).filter(Boolean).includes(slideActiveClass) &&
                    !slideActiveCooldown
                  ) {
                    slideActiveCooldown = true;
                    setTimeout(function () {
                      slideActiveCooldown = false;
                      var nextSlide = realSlides[idx + 1];
                      if (nextSlide && !nextSlide.classList.contains(slideActiveClass)) {
                        firePeekIfNew();
                      }
                    }, 300);
                    fireViewIfNew();
                  }
                } else {
                  // Native slideshow: fire when aria-hidden flips to false.
                  if (
                    mutation.attributeName === 'aria-hidden' &&
                    slide.getAttribute('aria-hidden') === 'false' &&
                    mutation.oldValue === 'true' &&
                    container.offsetParent !== null
                  ) {
                    pushPromoEvent('view_promotion', promo);
                  }
                }
              });
            });

            observer.observe(slide, {
              attributes: true,
              attributeOldValue: true,
              attributeFilter: [slideActiveClass ? 'class' : 'aria-hidden'],
            });
          });
        }

        // When observeViewport is true, defer the entire initContainer() call until
        // the section scrolls into view — this also guarantees Slick is initialised.
        // Fall back to immediate execution if IntersectionObserver is unavailable.
        if (observeViewport && 'IntersectionObserver' in window) {
          // Observe a stable ancestor instead of the container itself, because
          // lazy-loaded sections (lazyload-section) replace their innerHTML after
          // the initial render, which would detach the element our IO is watching.
          var observeTarget = container.closest('lazyload-section, [id^="shopify-section"]') || container;
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                io.disconnect();
                // Re-query the container — it may have been replaced by lazyload-section.
                container = document.querySelector(selector) || container;
                initContainer();
              }
            });
          }, { threshold: 0.1 });
          io.observe(observeTarget);
        } else {
          initContainer();
        }
      });
    }

    // For slideActiveClass sections: always defer setup by one tick so Slick has time
    // to initialise. setTimeout(0) from a deferred script fires AFTER DOMContentLoaded
    // and jQuery .ready(), which guarantees Slick has added .slick-slide / .slick-active.
    // This also ensures the page layout is stable before the IntersectionObserver
    // (observeViewport) evaluates element positions.
    if (slideActiveClass) {
      setTimeout(setup, 0);
    } else {
      setup();
    }
  }

  // Grid/carousel mode: each item with data-promo-creative / data-promo-slot attributes
  // gets its own IntersectionObserver so view_promotion fires only when that item is visible.
  function initGridTracking(options) {
    var containerSelector = options.containerSelector;
    var itemSelector      = options.itemSelector || '[data-promo-slot]';
    var linkSelector      = options.linkSelector || null;
    var promotionName     = options.promotionName || '';

    if (!promotionName) return;

    var container = document.querySelector(containerSelector);
    if (!container) return;

    var items = Array.from(container.querySelectorAll(itemSelector));
    if (!items.length) return;

    items.forEach(function(item) {
      var io = new IntersectionObserver(function(entries, obs) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) return;
          obs.disconnect();
          pushPromoEvent('view_promotion', {
            promotion_id:   item.dataset.promoId       || undefined,
            promotion_name: promotionName             || undefined,
            creative_name:  item.dataset.promoCreative || undefined,
            creative_slot:  item.dataset.promoSlot     || undefined
          });
        });
      }, { threshold: 0.1 });
      io.observe(item);
    });

    container.addEventListener('click', function(e) {
      var trigger = linkSelector ? e.target.closest(linkSelector) : e.target;
      if (linkSelector && !trigger) return;
      var item = (trigger || e.target).closest(itemSelector);
      if (!item) return;
      var promo = {
        promotion_id:   item.dataset.promoId       || undefined,
        promotion_name: promotionName             || undefined,
        creative_name:  item.dataset.promoCreative || undefined,
        creative_slot:  item.dataset.promoSlot     || undefined
      };
      try {
        sessionStorage.setItem(STORAGE_KEYS.promotion_id,   promo.promotion_id   || '');
        sessionStorage.setItem(STORAGE_KEYS.promotion_name, promo.promotion_name || '');
        sessionStorage.setItem(STORAGE_KEYS.creative_name,  promo.creative_name || '');
        sessionStorage.setItem(STORAGE_KEYS.creative_slot,  promo.creative_slot || '');
      } catch (_) {}
      pushPromoEvent('select_promotion', promo);
    });
  }

  // Route to the correct init function based on options shape.
  function dispatchTracking(options) {
    if (typeof options.containerSelector === 'string') {
      initGridTracking(options);
    } else {
      initPromoTracking(options);
    }
  }

  window.PromotionTracking = {
    /**
     * Reads the last clicked promotion from localStorage as a plain object.
     * Call this on landing pages (e.g. PDP) to retrieve the source promotion.
     * Returns null if nothing has been stored yet.
     */
    getLastClicked: function () {
      try {
        var name = sessionStorage.getItem(STORAGE_KEYS.promotion_name);
        if (name === null) return null;
        return {
          promotion_id:   sessionStorage.getItem(STORAGE_KEYS.promotion_id)   || undefined,
          promotion_name: name || undefined,
          creative_name:  sessionStorage.getItem(STORAGE_KEYS.creative_name)  || undefined,
          creative_slot:  sessionStorage.getItem(STORAGE_KEYS.creative_slot)  || undefined,
        };
      } catch (_) {
        return null;
      }
    },

    /**
     * Clears all stored promotion click keys from localStorage.
     */
    clearLastClicked: function () {
      try {
        Object.keys(STORAGE_KEYS).forEach(function (k) {
          sessionStorage.removeItem(STORAGE_KEYS[k]);
        });
      } catch (_) {}
    },

    /**
     * Directly push a promotion event to the dataLayer.
     */
    push: pushPromoEvent,

    /**
     * Set up tracking for a slider/banner section (containerSelectors array) or a
     * grid/carousel section (containerSelector string). Mode is auto-detected.
     */
    init: function (options) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
          dispatchTracking(options);
        });
      } else {
        dispatchTracking(options);
      }
    },

    /**
     * Set up view_promotion and select_promotion tracking for a static grid or carousel.
     * Items must carry data-promo-creative and data-promo-slot attributes.
     */
    initGrid: function (options) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () {
          initGridTracking(options);
        });
      } else {
        initGridTracking(options);
      }
    },
  };

  // Drain anything pushed to the queue by inline scripts before this deferred script loaded.
  // Because this script is deferred, the DOM is fully parsed and all inline scripts above
  // this point have already executed, so the queue is complete.
  var queue = window.PromotionTrackingQueue || [];
  queue.forEach(function (options) {
    dispatchTracking(options);
  });
  // Reset to a live object so future pushes (e.g. from second section) also get processed.
  window.PromotionTrackingQueue = { push: function (options) { dispatchTracking(options); } };
})();
